/**
 * Shared OSSLT Module Map pin math — used by the student journey (coords only)
 * and the authoring Module Map editor. Keep student chrome / captions out of here.
 */

export const OSSLT_MAP_ASSETS = {
  mapFramePlaceholder:
    'https://eqao.vretta.com/authoring/user_uploads/6526/authoring/Group (3)/1607447223258/Group (3).png',
  mapImage:
    'https://eqao.vretta.com/authoring/user_uploads/21/authoring/Map__1_/1616291389169/Map__1_.png',
  pointer:
    'https://eqao.vretta.com/authoring/user_uploads/6526/authoring/pointer/1606942399055/pointer.png',
} as const;

export interface IMmLoc {
  x: number;
  y: number;
}

export interface IResolveMmLocOptions {
  /** Preserve student display rewrite of x === -6 → -4.5 (SJ / NFLD legacy). */
  applyLegacyMmLocOverride?: boolean;
}

export const LEGACY_MM_LOC_X_OVERRIDE = -6;
export const LEGACY_MM_LOC_X_DISPLAY = -4.5;

export function asMmLoc(raw: any): IMmLoc | null {
  if (!raw || raw.x === undefined || raw.y === undefined) {
    return null;
  }
  const x = +raw.x;
  const y = +raw.y;
  if (Number.isNaN(x) || Number.isNaN(y)) {
    return null;
  }
  return { x, y };
}

/** Display-only resolve — does not mutate stored meta. */
export function resolveMmLocForDisplay(
  raw: any,
  options: IResolveMmLocOptions = {}
): IMmLoc | null {
  const loc = asMmLoc(raw);
  if (!loc) {
    return null;
  }
  if (options.applyLegacyMmLocOverride && loc.x === LEGACY_MM_LOC_X_OVERRIDE) {
    return { x: LEGACY_MM_LOC_X_DISPLAY, y: loc.y };
  }
  return loc;
}

/** Popup blurb offset used when MM_LOC2 is absent (student genModulesFromModuleMap). */
export function derivedMmLoc2(mmLoc: IMmLoc): IMmLoc {
  return {
    y: +mmLoc.y + 4,
    x: +mmLoc.x - 3,
  };
}

export function resolveMmLoc2ForDisplay(
  meta: { MM_LOC?: any; MM_LOC2?: any },
  options: IResolveMmLocOptions = {}
): IMmLoc | null {
  const explicit = asMmLoc(meta?.MM_LOC2);
  if (explicit) {
    return explicit;
  }
  const pin = resolveMmLocForDisplay(meta?.MM_LOC, options);
  return pin ? derivedMmLoc2(pin) : null;
}

export function mmLocToCssPercent(loc: IMmLoc): { bottom: string; right: string } {
  return {
    bottom: loc.y + '%',
    right: loc.x + '%',
  };
}

/** Round to one decimal — matches authoring drag commit precision. */
export function roundMmCoord(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Convert a client point inside the map frame to MM_LOC {x,y}
 * using the same bottom/right % contract as the student renderer.
 */
export function clientPointToMmLoc(
  clientX: number,
  clientY: number,
  frameRect: { left: number; top: number; width: number; height: number }
): IMmLoc {
  const relX = clientX - frameRect.left;
  const relY = clientY - frameRect.top;
  const rightPct = ((frameRect.width - relX) / frameRect.width) * 100;
  const bottomPct = ((frameRect.height - relY) / frameRect.height) * 100;
  return {
    x: roundMmCoord(rightPct),
    y: roundMmCoord(bottomPct),
  };
}

export function nudgeMmLoc(
  loc: IMmLoc,
  dx: number,
  dy: number
): IMmLoc {
  return {
    x: roundMmCoord(loc.x + dx),
    y: roundMmCoord(loc.y + dy),
  };
}

export interface IMapPinSourcePartition {
  mapMetaItemId?: number;
  description?: string;
  questions?: Array<number | string>;
}

export interface IResolvedMapPinItem {
  itemId: number;
  partitionDescription?: string;
  source: 'mapMetaItemId' | 'MM_TXT';
  mmLoc: IMmLoc;
  mmLoc2: IMmLoc;
  mmLocIsLegacyOverride: boolean;
}

/**
 * Resolve which question supplies map meta for a partition
 * (mapMetaItemId surrogate, else first section item with MM_TXT).
 */
export function resolveMapMetaQuestionId(
  partition: IMapPinSourcePartition,
  getQuestion: (id: number) => { meta?: any } | undefined
): { itemId: number; source: 'mapMetaItemId' | 'MM_TXT' } | null {
  if (partition.mapMetaItemId) {
    return { itemId: +partition.mapMetaItemId, source: 'mapMetaItemId' };
  }
  const qids = partition.questions || [];
  for (const qid of qids) {
    const question = getQuestion(+qid);
    if (question?.meta?.MM_TXT) {
      return { itemId: +qid, source: 'MM_TXT' };
    }
  }
  return null;
}

export function buildResolvedMapPin(
  partition: IMapPinSourcePartition,
  getQuestion: (id: number) => { meta?: any } | undefined,
  options: IResolveMmLocOptions = { applyLegacyMmLocOverride: true }
): IResolvedMapPinItem | null {
  const resolved = resolveMapMetaQuestionId(partition, getQuestion);
  if (!resolved) {
    return null;
  }
  const question = getQuestion(resolved.itemId);
  const rawLoc = asMmLoc(question?.meta?.MM_LOC);
  if (!rawLoc) {
    return null;
  }
  const mmLoc = resolveMmLocForDisplay(rawLoc, options) || rawLoc;
  const mmLoc2 =
    resolveMmLoc2ForDisplay(question?.meta || {}, options) || derivedMmLoc2(mmLoc);
  return {
    itemId: resolved.itemId,
    partitionDescription: partition.description,
    source: resolved.source,
    mmLoc,
    mmLoc2,
    mmLocIsLegacyOverride: rawLoc.x === LEGACY_MM_LOC_X_OVERRIDE,
  };
}
